// Search-engine copy for every public page. One source of truth for:
//  - runtime <title>/<meta> updates on SPA navigation (lib/seo.js)
//  - build-time static HTML snapshots, sitemap.xml and JSON-LD
//    (scripts/seo-postbuild.js)
// Visitors never see `body` — it is only in the raw HTML crawlers download and
// is removed the moment React mounts.

export const SITE = {
  name: 'GoldenBidX',
  url: 'https://goldenbidx.com',
  tagline: 'Online player auction software for cricket & sports tournaments',
  ogImage: 'https://goldenbidx.com/og-banner.jpg',
  ogSquare: 'https://goldenbidx.com/og-square.jpg',
  logo: 'https://goldenbidx.com/logo512.png',
  email: 'contactus@goldenbidx.com',
  sameAs: ['https://www.youtube.com/@GoldenBidX'],
};

// path → page copy. `body` is an array of { h2, p } sections rendered only in
// the crawler snapshot; keep it factual — it must match what the live page says.
export const PAGES = [
  {
    path: '/',
    title: 'GoldenBidX — Online Player Auction Software for Cricket & Sports Tournaments',
    description:
      'IPL-style online player auction for cricket and sports tournaments: UPI registration, live bidding, team budgets, YouTube overlay, exports. Free up to 4 teams.',
    h1: 'Run live player auctions like a pro',
    intro:
      'GoldenBidX is online player auction software for local cricket leagues, corporate tournaments and sports clubs. Players register from their phone, teams bid live, budgets are tracked automatically and the results are exported in one tap.',
    body: [
      {
        h2: 'Player registration with UPI payment',
        p: 'Share one link. Players sign up with a photo, role and stats, pay the registration fee by UPI and upload the screenshot. You verify them from the Organizer Console — only verified players enter the auction.',
      },
      {
        h2: 'Live bidding with team budgets',
        p: 'Smart Random player pick with a mystery reveal, threshold-based bid increments, countdown timer, icon-player tier, Fast Track for unsold players, undo bid and undo sale. Team purses, squad sizes and minimum-squad rules are enforced automatically.',
      },
      {
        h2: 'Remote bidding for team owners',
        p: 'Optionally give each team owner a private link so they bid from their own phone while you run the room. The auctioneer still starts, sells and undoes.',
      },
      {
        h2: 'Spectator link and YouTube streaming overlay',
        p: 'Anyone can follow every bid live on the public auction page. Add the broadcast overlay to OBS or any streaming tool in one of five styles, with your sponsors’ logos rotating on screen.',
      },
      {
        h2: 'Results, exports and analytics',
        p: 'Excel of all sales, designed squad posters per team, a shareable results card and an audit trail of every action. Nothing to install — it runs in the browser.',
      },
      {
        h2: 'Pricing',
        p: 'Events with up to 4 teams are completely free with every feature. Larger events pay a one-time fee per event (from ₹799 for up to 6 teams) by UPI. No subscriptions, no per-player charges.',
      },
    ],
    keywords: [
      'online player auction',
      'cricket auction software',
      'player auction app',
      'IPL style auction for local cricket',
      'tournament team auction',
      'box cricket auction app',
      'corporate cricket league auction',
      'remote bidding for team owners',
      'custom registration form for player auction',
    ],
  },
  {
    path: '/help',
    title: 'Help Center — How to run a player auction with GoldenBidX',
    description:
      'Video guides and FAQs: create an event, collect registrations with UPI, set up teams and budgets, run live bidding, stream to YouTube and export results.',
    h1: 'Help Center',
    intro:
      'Everything you need to run your first cricket or sports player auction with GoldenBidX — short video guides for each step, answers to common questions and how to reach support.',
    body: [
      {
        h2: 'Getting started',
        p: 'Sign up free, create your event (name, teams, registration fee, dates), share the registration link, verify players, then open Auction Setup on auction day.',
      },
      {
        h2: 'Running the auction',
        p: 'Pick a player with Smart Random, start bidding, click a team to bid, SOLD or UNSOLD. Use Fast Track for a second round of unsold players and Undo if something goes wrong.',
      },
      {
        h2: 'Streaming and sharing',
        p: 'Copy the public link for spectators and the overlay link for OBS / YouTube Live. After the auction the public link becomes the results page.',
      },
    ],
    faq: true,
    videos: true,
  },
  {
    path: '/tournaments',
    title: 'Live & upcoming cricket auctions — GoldenBidX tournaments',
    description:
      'Browse tournaments running their player auction on GoldenBidX. Watch live bidding, see final squads and register for upcoming events.',
    h1: 'Tournaments on GoldenBidX',
    intro:
      'Public cricket and sports tournaments that run their player auctions on GoldenBidX. Live auctions can be watched by anyone; completed ones show the final squads and results.',
    body: [],
  },
  {
    path: '/demo',
    title: 'Try the live auction demo — GoldenBidX',
    description:
      'Play with a sample cricket auction: pick players, place bids, sell and undo. No sign-up needed.',
    h1: 'Interactive demo',
    intro:
      'A sandbox auction with sample players and teams so you can try Smart Random, bidding, SOLD/UNSOLD and undo before creating your own event.',
    body: [],
  },
  {
    path: '/privacy',
    title: 'Privacy Policy — GoldenBidX',
    description: 'How GoldenBidX collects, uses and protects organizer and player data.',
    h1: 'Privacy Policy',
    intro: 'How GoldenBidX handles organizer accounts, player registrations and payment proofs.',
    body: [],
  },
  {
    path: '/terms',
    title: 'Terms of Service — GoldenBidX',
    description:
      'Terms for organizers and players using GoldenBidX to run and join player auctions.',
    h1: 'Terms of Service',
    intro: 'The terms under which organizers run events and players register on GoldenBidX.',
    body: [],
  },
];

export const pageFor = (pathname) => {
  const clean = (pathname || '/').replace(/\/+$/, '') || '/';
  return PAGES.find((p) => p.path === clean) || null;
};

// Routes that must never be indexed (operator / private / transient).
export const NOINDEX_PREFIXES = ['/dashboard', '/setup', '/console', '/bid/', '/reset-password'];
export const isNoIndex = (pathname) =>
  NOINDEX_PREFIXES.some((p) => (pathname || '').startsWith(p)) ||
  /\/a\/[^/]+\/overlay$/.test(pathname || '');
